import { useCallback, useEffect, useMemo, useState } from 'react'

import { Plus } from '@gravity-ui/icons'
import { Button, InputGroup, Label, Spinner, TextField, toast } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { z } from 'zod'

import { reportsApi } from '@/api/reports.api'
import { DriverAutocomplete } from '@/components/common/DriverAutocomplete'
import { VehicleAutocomplete } from '@/components/common/VehicleAutocomplete'
import { Card, CardContent } from '@/components/ui/Card'
import { DateInput } from '@/components/ui/DateInput'
import { PageHeader } from '@/components/ui/PageHeader'
import useUnsavedChangesGuard from '@/hooks/useUnsavedChangesGuard'
import { useRatesStore } from '@/store/rates.store'
import { mapApiToUi, useReportsStore } from '@/store/reports.store'
import type { ModifyReportRequest } from '@/types/report'
import type { Report as UiReport } from '@/types/report.types'
import type { ReportStatus } from '@/types/report.types'
import { calcPLN } from '@/utils/currency'
import { todayISO } from '@/utils/dates'

import { ExpenseTable } from './components/ExpenseTable'

const expenseRowSchema = z.object({
  id: z.string(),
  date: z.string().min(1, 'Укажите дату'),
  type: z.string().min(1, 'Укажите тип расхода'),
  documentNumber: z.string().min(1, 'Укажите номер документа'),
  country: z.string().min(1, 'Выберите страну'),
  amount: z.number({ error: 'Сумма должна быть > 0' }).positive('Сумма должна быть > 0'),
  currency: z.string().min(1, 'Выберите валюту'),
  exchangeRate: z.number({ error: 'Укажите курс' }).positive('Укажите курс'),
  nbpTable: z.string().optional(),
  amountPLN: z.number(),
  isIncluded: z.boolean().optional(),
})

const reportSchema = z.object({
  identifier: z.string().min(1, 'Укажите ID (Identifier)'),
  driverId: z.string().min(1, 'Выберите водителя'),
  vehicleId: z.string().min(1, 'Выберите транспорт'),
  dateFrom: z.string().min(1, 'Укажите дату начала'),
  dateTo: z.string().min(1, 'Укажите дату окончания'),
  rows: z.array(expenseRowSchema).min(1, 'Добавьте хотя бы одну строку расходов'),
}).refine(d => !d.dateTo || !d.dateFrom || d.dateTo >= d.dateFrom, {
  message: 'Дата окончания не может быть раньше даты начала',
  path: ['dateTo'],
})

type FormValues = z.infer<typeof reportSchema>;

export default function ReportForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)

  const { addReport, updateReport, isLoading: isStoreLoading } = useReportsStore()
  const { fetchRate: fetchStoreRate } = useRatesStore()

  const [report, setReport] = useState<UiReport | null>(null)
  const [isLoadingReport, setIsLoadingReport] = useState(isEdit)

  const { register, control, handleSubmit, watch, setValue, reset, getValues, formState: { isDirty } } =
    useForm<FormValues>({
      resolver: zodResolver(reportSchema),
      defaultValues: {
        identifier: '',
        driverId: '',
        vehicleId: '',
        dateFrom: todayISO(),
        dateTo: todayISO(),
        rows: [
          {
            id: uuid(),
            date: todayISO(),
            type: '',
            documentNumber: '',
            country: 'PL',
            amount: 0,
            currency: 'PLN',
            exchangeRate: 1,
            nbpTable: '—',
            amountPLN: 0,
            isIncluded: true,
          },
        ],
      },
    })

  const { dialog: unsavedDialog, guardRef, requestBack } = useUnsavedChangesGuard(isDirty)

  useEffect(() => {
    const loadData = async () => {
      if (isEdit && id) {
        setIsLoadingReport(true)
        try {
          const reportData = await reportsApi.getReport(id)
          const uiReport = mapApiToUi(reportData)
          setReport(uiReport)
          reset({
            identifier: uiReport.identifier,
            driverId: uiReport.driverId,
            vehicleId: uiReport.vehicleId,
            dateFrom: uiReport.dateFrom,
            dateTo: uiReport.dateTo,
            rows: uiReport.rows,
          })
        } catch (error) {
          console.error('Failed to fetch report:', error)
          toast.danger(t('reports.toast.fetch_error'))
        } finally {
          setIsLoadingReport(false)
        }
      }
    }

    loadData()
  }, [id, isEdit, reset, t])

  const initialDriver = useMemo(() => {
    if (!report) return null
    return {
      ID: report.driverId,
      Name: report.driverName || '',
      Phone: report.driverPhone || '',
      Vehicle: null,
    }
  }, [report])

  const initialVehicle = useMemo(() => {
    if (!report) return null
    return {
      ID: report.vehicleId,
      Number: report.vehiclePlate || '',
      Model: report.vehicleBrand || '',
      Make: 0,
    }
  }, [report])

  const { fields, append, remove } = useFieldArray({ control, name: 'rows' })
  const rows = watch('rows')

  const fetchRate = useCallback(async (rowIndex: number, currency: string, date: string) => {
    if (!currency || !date) return
    const currentRows = getValues('rows')
    const rowId = currentRows[rowIndex]?.id
    if (!rowId) return

    try {
      const rateItem = await fetchStoreRate(currency, date)
      if (!rateItem) return

      const rate = rateItem.Rate
      const effectDate = rateItem.Date || date
      const tableNumber = '—'
      const tableStr = currency === 'PLN' ? '—' : `Tab.${tableNumber}/NBP/${effectDate.slice(0, 4)} z dn.${effectDate}`

      setValue(`rows.${rowIndex}.exchangeRate`, rate, { shouldValidate: true })
      setValue(`rows.${rowIndex}.nbpTable`, tableStr, { shouldValidate: true })
      const currentRows = getValues('rows')
      const amount = currentRows[rowIndex]?.amount ?? 0
      setValue(`rows.${rowIndex}.amountPLN`, calcPLN(Number(amount), rate))
    } catch {
      console.warn('NBP fetch failed — enter rate manually')
    }
  }, [getValues, setValue, fetchStoreRate])

  const handleAddRow = useCallback(() => {
    append({
      id: uuid(),
      date: todayISO(),
      type: '',
      documentNumber: '',
      country: 'PL',
      amount: 0,
      currency: 'PLN',
      exchangeRate: 1,
      nbpTable: '—',
      amountPLN: 0,
      isIncluded: true,
    })
  }, [append])

  if (isLoadingReport) {
    return (
      <div className="flex justify-center items-center h-[400px]">
        <Spinner size="lg" />
      </div>
    )
  }

  const submitWith = (status: ReportStatus) => handleSubmit(async (data: FormValues) => {
    console.log('Form data:', data)
    const apiData: ModifyReportRequest = {
      Identifier: data.identifier,
      DriverID: data.driverId,
      VehicleID: data.vehicleId,
      StartDate: data.dateFrom,
      EndDate: data.dateTo,
      Items: data.rows.map(r => ({
        Date: r.date,
        Expense: r.type,
        Document: r.documentNumber,
        Country: r.country as any, // Cast to match spec type
        Cost: r.amount,
        Currency: r.currency as any,
        Rate: r.exchangeRate,
        RateDate: r.nbpTable || '',
        Exclude: r.isIncluded === false,
      })),
    }

    // Note: status is currently ignored as it's missing from the ModifyReportRequest spec
    console.log('Submitting with status:', status)

    try {
      if (isEdit && id) {
        await updateReport(id, apiData)
      } else {
        await addReport(apiData)
      }
      guardRef.current = false
      navigate('/reports')
    } catch {
      // Error handled by store toast
    }
  }, errors => {
    console.error('Validation errors:', errors)
    toast.danger(t('common.form_validation_error'))
  })

  return (
    <form className="p-5 space-y-5" onSubmit={e => e.preventDefault()}>
      {unsavedDialog}
      {/* Header */}
      <PageHeader
        onBack={requestBack}
        title={isEdit ? t('reports.edit_report') : t('reports.new_report')}
        subtitle="Ksiegowe koszty gotowkowe do wyjazdu sluzbowego"
      />

      {/* Header fields */}
      <Card>
        <CardContent className="p-2">
          <h2 className="text-md font-black mb-4">{t('reports.form.header_info')}</h2>
          <div className="mb-4">
            <Controller
              name="identifier"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  isRequired
                  isInvalid={!!fieldState.error}
                >
                  <Label>{t('reports.identifier', 'Номер документа')}</Label>
                  <InputGroup variant={'secondary'}>
                    <InputGroup.Prefix>
                      <span className="text-zinc-500 font-bold px-2">#</span>
                    </InputGroup.Prefix>
                    <InputGroup.Input
                      {...field}
                      placeholder="4/2026"
                    />
                  </InputGroup>
                </TextField>
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Controller
              name="driverId"
              control={control}
              render={({ field, fieldState }) => (
                <DriverAutocomplete
                  label={t('reports.driver')}
                  isRequired={true}
                  selectedId={field.value}
                  initialValue={initialDriver}
                  onSelectionChange={driver => {
                    field.onChange(driver?.ID || '')
                    if (driver?.Vehicle) {
                      setValue('vehicleId', driver.Vehicle.ID, { shouldValidate: true })
                    }
                  }}
                  placeholder={t('reports.form.driver_placeholder')}
                  isInvalid={!!fieldState.error}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="vehicleId"
              control={control}
              render={({ field, fieldState }) => (
                <VehicleAutocomplete
                  label={t('reports.vehicle')}
                  isRequired={true}
                  selectedId={field.value}
                  initialValue={initialVehicle}
                  onSelectionChange={vehicle => {
                    field.onChange(vehicle?.ID || '')
                  }}
                  placeholder={t('reports.form.vehicle_placeholder')}
                  isInvalid={!!fieldState.error}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="dateFrom"
              control={control}
              render={({ field, fieldState }) => (
                <DateInput
                  {...field}
                  label={t('reports.form.date_from')}
                  required
                  isInvalid={!!fieldState.error}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="dateTo"
              control={control}
              render={({ field, fieldState }) => (
                <DateInput
                  {...field}
                  label={t('reports.form.date_to')}
                  required
                  isInvalid={!!fieldState.error}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Expense rows */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-zinc-100 dark:border-zinc-800">
        <h2 className="font-black text-lg">{t('reports.form.rows_title')}</h2>
        <Button
          variant="secondary"
          onClick={handleAddRow}
        >
          <Plus />
          {t('reports.form.add_row')}
        </Button>
      </div>

      <ExpenseTable
        fields={fields}
        rows={rows}
        register={register}
        control={control as any}
        setValue={setValue as any}
        onRemove={remove}
        onFetchRate={fetchRate}
        onAdd={handleAddRow}
      />
      <div className="px-5 py-4 flex items-center justify-end border-t border-zinc-100 dark:border-zinc-800">
        <div className="flex gap-2">
          <Button
            variant="ghost" isDisabled={isStoreLoading || isLoadingReport}
            onClick={requestBack}
          >
            {t('common.cancel')}
          </Button>
          {/*<Button variant="ghost" isDisabled={isStoreLoading || isLoadingReport} onClick={() => submitWith('draft')()}>*/}
          {/*  {t('reports.form.save_draft')}*/}
          {/*</Button>*/}
          <Button
            isDisabled={isStoreLoading || isLoadingReport}
            onClick={() => submitWith('pending')()}
          >
            {t('reports.form.submit')}
          </Button>
        </div>
      </div>
    </form>
  )
}
